import React, {useEffect, useState} from 'react';
import Spinner from '../Spinner';
import {formatDayDate} from './TripDayCard';
import {
    submitTripWearReport,
    TripAnalytics,
    TripAnalyticsItem,
} from '../../service/TripApi';

interface Props {
    tripId: string;
    analytics: TripAnalytics;
    /** Отчёт отправлен — карточке нужно перечитать итоги и список вещей. */
    onReported: (analytics: TripAnalytics) => void;
    onError: (message: string) => void;
}

/**
 * Итоги завершённой поездки: что реально носили и что зря везли.
 *
 * <p>Форма предзаполнена планом: обычно человек надевает то, что собирался, и согласие
 * должно стоить одного нажатия. Снятая галочка не стирает уже записанную носку — итоги
 * поездки не переписывают историю гардероба задним числом, но вещь уходит в «не
 * пригодилось» и попадает в подсказку на следующую поездку.</p>
 */
const TripSummarySection: React.FC<Props> = ({tripId, analytics, onReported, onError}) => {
    const [worn, setWorn] = useState<Record<string, string[]>>(() => initialWorn(analytics));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setWorn(initialWorn(analytics));
    }, [analytics]);

    const toggle = (date: string, clothingItemId: string) => {
        setWorn(prev => {
            const current = prev[date] ?? [];
            const next = current.includes(clothingItemId)
                ? current.filter(id => id !== clothingItemId)
                : [...current, clothingItemId];
            return {...prev, [date]: next};
        });
    };

    const save = async () => {
        setSaving(true);
        try {
            const days = analytics.days.map(day => ({date: day.date, itemIds: worn[day.date] ?? []}));
            onReported(await submitTripWearReport(tripId, days));
        } catch (e) {
            onError(reportErrorText(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-5" data-testid="trip-summary">
            {analytics.days.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="trip-summary-empty">
                    В этой поездке не было плана образов — отмечать нечего.
                </p>
            ) : (
                <div className="space-y-3">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        Отметьте, что действительно носили. По умолчанию отмечен план поездки.
                    </p>
                    <ul className="grid gap-3" data-testid="trip-summary-days">
                        {analytics.days.map(day => (
                            <li key={day.dayId}
                                className="p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 space-y-2">
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                    {formatDayDate(day.date)}
                                    {day.eventLabel && (
                                        <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                                            {day.eventLabel}
                                        </span>
                                    )}
                                </p>
                                {day.plannedItems.length === 0 ? (
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Образ не собирался</p>
                                ) : (
                                    <ul className="space-y-1">
                                        {day.plannedItems.map(item => (
                                            <li key={`${day.dayId}-${item.clothingItemId}`}>
                                                <label className="flex items-center gap-3 min-h-[44px] text-sm text-gray-800 dark:text-gray-100">
                                                    <input
                                                        type="checkbox"
                                                        checked={(worn[day.date] ?? []).includes(item.clothingItemId)}
                                                        onChange={() => toggle(day.date, item.clothingItemId)}
                                                        data-testid={`trip-summary-worn-${day.dayId}-${item.clothingItemId}`}
                                                        className="w-5 h-5 min-w-[20px] accent-brand-500"
                                                    />
                                                    {item.name ?? 'Без названия'}
                                                </label>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ul>
                    <button type="button" onClick={save} disabled={saving} data-testid="trip-summary-save"
                            className="btn-primary min-h-[44px]">
                        {saving ? 'Сохраняем…' : analytics.reportedAt ? 'Обновить итоги' : 'Сохранить итоги'}
                    </button>
                    {saving && <Spinner/>}
                </div>
            )}

            {analytics.reportedAt && (
                <div className="grid gap-3 sm:grid-cols-2" data-testid="trip-summary-totals">
                    <SummaryCard title="Взяли" testId="trip-summary-taken" items={analytics.taken}/>
                    <SummaryCard title="Носили" testId="trip-summary-used" items={analytics.used}/>
                    <SummaryCard title="Не пригодилось" testId="trip-summary-unnecessary"
                                 items={analytics.unnecessary}/>
                    <SummaryCard title="Не брали" testId="trip-summary-skipped" items={analytics.skipped}/>
                </div>
            )}

            {analytics.recommendations.length > 0 && (
                <section className="space-y-2" data-testid="trip-summary-recommendations">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        В следующую поездку можно не брать
                    </h3>
                    <ul className="space-y-1">
                        {analytics.recommendations.map(item => (
                            <li key={item.tripItemId} className="text-sm text-gray-600 dark:text-gray-300">
                                {item.name ?? 'Без названия'}
                                {item.strongHint && (
                                    <span className="ml-2 text-xs text-amber-700 dark:text-amber-400">
                                        не пригодилась уже {item.unusedTripCount + 1} раз(а)
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
};

const SummaryCard: React.FC<{title: string; testId: string; items: TripAnalyticsItem[]}> =
    ({title, testId, items}) => (
        <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800"
             data-testid={testId}>
            <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
            <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{items.length}</p>
            {items.length > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {items.map(item => item.name ?? '—').join(', ')}
                </p>
            )}
        </div>
    );

/** Предзаполнение формы: что backend считает надетым (до отчёта — весь план дня). */
export function initialWorn(analytics: TripAnalytics): Record<string, string[]> {
    const map: Record<string, string[]> = {};
    analytics.days.forEach(day => {
        map[day.date] = [...day.wornItemIds];
    });
    return map;
}

/** Текст ошибки отправки отчёта: описание backend, иначе общий fallback. */
export function reportErrorText(err: unknown): string {
    const data = (err as {response?: {data?: {error?: string; description?: string}}} | undefined)?.response?.data;
    return data?.description?.trim() || data?.error?.trim() || 'Не удалось сохранить итоги поездки.';
}

export default TripSummarySection;
